import { type DragEvent, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { ArrowDownOutlined, ArrowUpOutlined, ExclamationCircleOutlined, HolderOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Dropdown, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import InfiniteScroller from 'components/molecules/InfiniteScroller';
import {
    DimensionDropdownPanel,
    HeaderTitle,
    InputKpiHeader,
    KpiDropdownPanel,
    StyledInfiniteTableWrapper,
    StyledSectionTabs,
    bumpInputValue,
    dimensionSelectionLimit,
    filterButtonClassName,
    filterButtonIconClassName,
    getDefaultInputValue,
    getPrimaryInputConfig,
    normalizeInputValue,
    recordSummaryClassName,
} from './performanceSummaryShared';
import Down from 'assets/svg/Down';
import usePerformanceSummaryKpis, {
    PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT,
    type PerformanceSummaryDrilldownFilter,
    type PerformanceSummaryOrderByItem,
} from './hooks/usePerformanceSummaryKpis';
import {
    filterPpaKpisByGranularity,
    formatPpaMetricValue,
    normalizePpaAttributeType,
    normalizePpaKpiRows,
    type PpaGranularityMappingRow,
    resolvePpaGranularityId,
} from 'utils/ppaHelpers';

type OverallPerformanceDimensionKey = string;
type KpiKey = string;
type DropdownItem = {
    key: string;
    label: string;
    is_default?: boolean;
};
type LensTableRow = {
    key: string;
    dimensionValues: Record<string, string>;
    kpiValues: Record<string, string>;
    metrics: Record<string, TrendMetric>;
    isTotal?: boolean;
};

type MetaOption = {
    id: string;
    label: string;
    dimension?: string;
    comparison_period?: string;
    is_default?: boolean;
    is_mandatory?: boolean;
    mandatory?: boolean;
    isMandatory?: boolean;
    is_required?: boolean;
    is_attribute?: boolean;
    input_list?: MetaInputConfig;
    /** Empty or omitted = valid for all granularities (v1 / v2). */
    granularity_id_list?: string[];
};

function lensAttributeSortActive(
    orderBy: PerformanceSummaryOrderByItem[] | undefined,
    dimensionConfig: MetaOption,
): 'asc' | 'desc' | null {
    const o = orderBy?.[0];
    if (o?.id_type === 'attribute_type' && o.id === dimensionConfig.id) return o.direction;
    return null;
}

function lensKpiSortActive(
    orderBy: PerformanceSummaryOrderByItem[] | undefined,
    kpiId: string,
): 'asc' | 'desc' | null {
    const o = orderBy?.[0];
    if (o?.id_type === 'kpi_id' && o.id === kpiId) return o.direction;
    return null;
}

type MetaInputBounds = {
    min_val?: number;
    max_val?: number;
};

type MetaInputConfig = {
    input_id: string;
    input_type?: string;
    default_value?: string | number | null;
    placeholder?: string;
    increment_rate?: number;
    bounds?: MetaInputBounds;
};

type MetaTab = {
    id: string;
    label: string;
    is_default?: boolean;
    group_by_options?: MetaOption[];
    column_options?: MetaOption[];
    granularity_column_mapping?: PpaGranularityMappingRow[];
};
type PerformanceByLensMetaFeature = {
    feature_id: string;
    options?: {
        tabs?: MetaTab[];
    };
};
type PerformanceByLensSectionProps = {
    metaFeature?: PerformanceByLensMetaFeature;
    drilldownFilters?: PerformanceSummaryDrilldownFilter[];
};
export type PerformanceByLensExportHandle = {
    getExportData: () => { headers: string[]; rows: string[][] };
};

type KpiItem = {
    kpi_id: string;
    value?: string | number;
    raw_value?: string | number | null;
    valuePrefix?: string;
    valueSuffix?: string;
    comparison_period?: string;
    growth_percentage?: number | null;
    trend_direction?: 'up' | 'down' | 'neutral';
};
type KpiNode = {
    dimension?: string;
    attribute_type?: string;
    ex_id?: string;
    description?: string;
    is_total?: boolean;
    kpis?: KpiItem[];
    data?: KpiNode[];
};

type TrendMetric = {
    value: string;
    raw_value: string | number | null;
    delta: string;
    direction: 'up' | 'down';
};

const DEFAULT_METRIC: TrendMetric = { value: '-', raw_value: '-', delta: '-', direction: 'up' };

const areStringListsEqual = (left: string[], right: string[]) =>
    left.length === right.length && left.every((value, index) => value === right[index]);

const getDimensionOptionKey = (option: Pick<MetaOption, 'id' | 'label'>) => `${option.id}::${option.label}`;

const isMandatoryOption = (option: MetaOption) => Boolean(option.is_mandatory);

const normalizeDimensionSelection = (keys: OverallPerformanceDimensionKey[], tabConfig?: MetaTab) => {
    const groupByOptions = tabConfig?.group_by_options ?? [];
    const validDimensionIds = new Set(groupByOptions.map((option) => getDimensionOptionKey(option)));
    const mandatoryDimensionIds = groupByOptions.filter((option) => isMandatoryOption(option)).map((option) => getDimensionOptionKey(option));
    const mandatoryDimensionSet = new Set(mandatoryDimensionIds);
    const defaultDimensionIds = groupByOptions
        .filter((option) => option.is_default || mandatoryDimensionSet.has(getDimensionOptionKey(option)))
        .map((option) => getDimensionOptionKey(option));
    const fallbackDefaultDimensionId = groupByOptions[0];
    const fallbackDefaultDimensionKey = fallbackDefaultDimensionId ? getDimensionOptionKey(fallbackDefaultDimensionId) : '';
    const deduped = Array.from(new Set(keys)).filter((key) => validDimensionIds.has(key));
    const selectionLimit = Math.max(dimensionSelectionLimit, defaultDimensionIds.length, mandatoryDimensionIds.length);
    if (deduped.length) {
        const nextSelection = [...mandatoryDimensionIds, ...deduped.filter((key) => !mandatoryDimensionSet.has(key))];
        return Array.from(new Set(nextSelection)).slice(0, selectionLimit);
    }
    if (defaultDimensionIds.length) return defaultDimensionIds.slice(0, selectionLimit);
    return fallbackDefaultDimensionKey ? [fallbackDefaultDimensionKey] : [];
};

const normalizeKpiSelection = (keys: KpiKey[], tabConfig?: MetaTab) => {
    const columnOptions = tabConfig?.column_options ?? [];
    const validKpiIds = new Set(columnOptions.map((option) => option.id));
    const mandatoryKpis = columnOptions
        .filter((option) => option.is_mandatory || option.mandatory || option.isMandatory || option.is_required)
        .map((option) => option.id);
    const mandatoryKpiSet = new Set(mandatoryKpis);
    const deduped = Array.from(new Set(keys)).filter((key) => validKpiIds.has(key));
    if (deduped.length) {
        const nextSelection = [...mandatoryKpis, ...deduped.filter((kpiId) => !mandatoryKpiSet.has(kpiId))];
        return Array.from(new Set(nextSelection));
    }

    const defaultKpis = columnOptions
        .filter((option) => option.is_default || mandatoryKpiSet.has(option.id))
        .map((option) => option.id);
    if (defaultKpis.length) return defaultKpis;

    return columnOptions[0]?.id ? [columnOptions[0].id] : [];
};

const withMetricAffixes = (value: string, prefix?: string, suffix?: string) => {
    if (value === '-') return value;

    let next = value;
    const normalizedPrefix = prefix?.trim();
    const normalizedSuffix = suffix?.trim();

    if (normalizedPrefix && !next.startsWith(normalizedPrefix)) next = `${normalizedPrefix}${next}`;
    if (normalizedSuffix && !next.endsWith(normalizedSuffix)) next = `${next}${normalizedSuffix}`;

    return next;
};

const formatRawMetricForExport = (rawValue: string) => {
    if (rawValue === '') return rawValue;
    const normalizedRawValue = rawValue.trim();
    const numericCandidate = normalizedRawValue.replace(/,/g, '');
    const parsedRawValue = Number(numericCandidate);
    if (!Number.isFinite(parsedRawValue)) return rawValue;
    return Number.isFinite(parsedRawValue) ? parsedRawValue.toFixed(2) : rawValue;
};

const metricFromKpi = (kpiId: string, kpi?: KpiItem): TrendMetric => {
    const rawValue = withMetricAffixes(formatPpaMetricValue(kpi?.value), kpi?.valuePrefix, kpi?.valueSuffix);
    const value =
        rawValue === '-' && normalizePpaAttributeType(kpiId) === 'growth_percentage' && kpi?.growth_percentage != null
            ? `${kpi.growth_percentage}%`
            : rawValue;
    return {
        value,
        raw_value: kpi?.raw_value ?? '',
        delta: kpi?.growth_percentage == null ? '-' : `${kpi.growth_percentage}%`,
        direction: kpi?.trend_direction === 'down' ? 'down' : 'up',
    };
};

const TrendMetricCell = ({ metric, isTotal }: { metric: TrendMetric; isTotal?: boolean }) => {
    const isUp = metric.direction === 'up';

    return (
        <div className="flex items-center justify-start gap-2">
            <span className={`text-base leading-[22px] text-black ${isTotal ? 'font-semibold' : 'font-normal'}`}>{metric.value}</span>
            {metric.delta === '-' ? null : (
                <div className="ml-1">
                    <span className={`text-base mr-1 ${isUp ? 'text-[#14b8a6]' : 'text-[#ff4d4f]'}`}>
                        {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    </span>
                    <span className={`text-base leading-[22px] text-black/65 ${isTotal ? 'font-semibold' : 'font-normal'}`}>{metric.delta}</span>
                </div>
            )}
        </div>
    );
};

const formatComparisonPeriodLabel = (comparisonPeriod?: string) => {
    const normalizedPeriod = comparisonPeriod?.trim().toLowerCase();
    if (!normalizedPeriod) return '';
    if (normalizedPeriod === 'pre_renovation') return 'Pre Renovation';
    if (normalizedPeriod === 'year') return 'YA';
    if (normalizedPeriod === 'month') return 'Month';
    if(normalizedPeriod === 'pre_event') return 'Pre Period';
};
const toLensRows = (nodes: KpiNode[], selectedIds: string[], selectedDimensions: MetaOption[]): LensTableRow[] => {
    const dimensionLookupKeysById = new Map(
        selectedDimensions.map((dimension) => [
            getDimensionOptionKey(dimension),
            [normalizePpaAttributeType(dimension.id), normalizePpaAttributeType(dimension.label)].filter(Boolean),
        ])
    );
    const rows: LensTableRow[] = [];
    const selectedDimensionAttrTypes = new Set(
        selectedDimensions
            .map((dimension) => normalizePpaAttributeType(dimension.id))
            .filter((attributeType): attributeType is string => Boolean(attributeType))
    );

    const walk = (treeNodes: KpiNode[], inheritedValuesByAttrType: Record<string, string>) => {
        treeNodes.forEach((node, index) => {
            const attrTypeKey = normalizePpaAttributeType(node.attribute_type);
            const currentValue = String(node.description ?? node.ex_id ?? '-');
            const valuesByAttrType = { ...inheritedValuesByAttrType };

            if (attrTypeKey) valuesByAttrType[attrTypeKey] = currentValue;

            const isSelectedDimensionNode = attrTypeKey ? selectedDimensionAttrTypes.has(attrTypeKey) : false;
            if (node.kpis?.length && (isSelectedDimensionNode || Boolean(node.is_total))) {
                const kpiMap = new Map((node.kpis ?? []).map((kpi) => [kpi.kpi_id, kpi]));
                const metrics = selectedIds.reduce<Record<string, TrendMetric>>((acc, id) => {
                    const kpi = kpiMap.get(id);
                    acc[id] = metricFromKpi(id, kpi);
                    return acc;
                }, {});
                const kpiValues = selectedIds.reduce<Record<string, string>>((acc, id) => {
                    acc[id] = metrics[id]?.value ?? '-';
                    return acc;
                }, {});
                const dimensionValues = selectedDimensions.reduce<Record<string, string>>((acc, dimension) => {
                    const dimensionKey = getDimensionOptionKey(dimension);
                    const lookupKeys = dimensionLookupKeysById.get(dimensionKey) ?? [];
                    const matchedKey = lookupKeys.find((key) => Boolean(valuesByAttrType[key]));
                    acc[dimensionKey] = matchedKey ? valuesByAttrType[matchedKey] : '-';
                    return acc;
                }, {});

                rows.push({
                    key: `${node.ex_id ?? node.description ?? 'row'}-${rows.length}-${index}`,
                    dimensionValues,
                    isTotal: Boolean(node.is_total),
                    kpiValues,
                    metrics,
                });
            }

            if (node.data?.length) walk(node.data, valuesByAttrType);
        });
    };

    walk(nodes, {});
    return rows;
};

const getComparisonPeriodByKpiId = (nodes: KpiNode[]) => {
    const periodByKpiId = new Map<string, string>();
    const walk = (treeNodes: KpiNode[]) => {
        treeNodes.forEach((node) => {
            (node.kpis ?? []).forEach((kpi) => {
                const normalizedPeriod = kpi.comparison_period?.trim();
                if (!normalizedPeriod || periodByKpiId.has(kpi.kpi_id)) return;
                periodByKpiId.set(kpi.kpi_id, normalizedPeriod);
            });
            if (node.data?.length) walk(node.data);
        });
    };
    walk(nodes);
    return periodByKpiId;
};

type PerformanceSectionHeaderProps = {
    title: string;
    activeDimensions: OverallPerformanceDimensionKey[];
    dimensionMenuItems: DropdownItem[];
    lockedDimensionIds: OverallPerformanceDimensionKey[];
    onDimensionSelect: (keys: OverallPerformanceDimensionKey[]) => void;
    selectedKpis: KpiKey[];
    kpiMenuItems: DropdownItem[];
    defaultKpis: KpiKey[];
    mandatoryKpis: KpiKey[];
    onKpiSelect: (keys: KpiKey[]) => void;
};

const PerformanceSectionHeader = ({
    title,
    activeDimensions,
    dimensionMenuItems,
    lockedDimensionIds,
    onDimensionSelect,
    selectedKpis,
    kpiMenuItems,
    defaultKpis,
    mandatoryKpis,
    onKpiSelect,
}: PerformanceSectionHeaderProps) => {
    const [isDimensionDropdownOpen, setIsDimensionDropdownOpen] = useState(false);
    const [dimensionSearch, setDimensionSearch] = useState('');
    const [isKpiDropdownOpen, setIsKpiDropdownOpen] = useState(false);
    const [draftKpiIds, setDraftKpiIds] = useState<KpiKey[]>(selectedKpis);
    const [draftKpiOrder, setDraftKpiOrder] = useState<KpiKey[]>(kpiMenuItems.map((kpi) => kpi.key));
    const [draggingKpiId, setDraggingKpiId] = useState<KpiKey | null>(null);
    const [draggingOverKpiId, setDraggingOverKpiId] = useState<KpiKey | null>(null);

    const normalizedDimensionSearch = dimensionSearch.trim().toLowerCase();
    const filteredDimensionItems = normalizedDimensionSearch
        ? dimensionMenuItems.filter((item) => item.label.toLowerCase().includes(normalizedDimensionSearch))
        : dimensionMenuItems;
    const selectedDimensionCount = activeDimensions.length;
    const dimensionSelectionSet = new Set(activeDimensions);
    const isAllDimensionsSelected =
        dimensionMenuItems.length > 0 && dimensionMenuItems.every((dimension) => dimensionSelectionSet.has(dimension.key));
    const defaultDimensionCount = useMemo(
        () => dimensionMenuItems.filter((dimension) => dimension.is_default).length,
        [dimensionMenuItems]
    );
    const lockedDimensionSet = useMemo(() => new Set(lockedDimensionIds), [lockedDimensionIds]);
    const effectiveDimensionLimit = Math.max(dimensionSelectionLimit, defaultDimensionCount, lockedDimensionIds.length);
    const isDimensionSelectionLimitReached = selectedDimensionCount >= effectiveDimensionLimit;
    const dimensionValidationMessage = isAllDimensionsSelected
        ? 'No change in dimensions possible'
        : `Select up to ${dimensionSelectionLimit} dimensions`;
    const kpiMenuItemById = useMemo(() => new Map(kpiMenuItems.map((item) => [item.key, item])), [kpiMenuItems]);
    const orderedKpiItems = useMemo(() => {
        const seen = new Set<KpiKey>();
        const ordered = draftKpiOrder
            .map((kpiId) => {
                seen.add(kpiId);
                return kpiMenuItemById.get(kpiId);
            })
            .filter((kpi): kpi is DropdownItem => Boolean(kpi));
        const missing = kpiMenuItems.filter((item) => !seen.has(item.key));
        return [...ordered, ...missing];
    }, [draftKpiOrder, kpiMenuItemById, kpiMenuItems]);
    const allKpisSelected = orderedKpiItems.length > 0 && draftKpiIds.length === orderedKpiItems.length;
    const mandatoryKpiSet = useMemo(() => new Set(mandatoryKpis), [mandatoryKpis]);

    useEffect(() => {
        if (!isKpiDropdownOpen) return;
        const kpiOrder = kpiMenuItems.map((kpi) => kpi.key);
        const validKpiIds = new Set(kpiOrder);
        setDraftKpiOrder(kpiOrder);
        setDraftKpiIds(selectedKpis.filter((kpiId) => validKpiIds.has(kpiId)));
    }, [isKpiDropdownOpen, kpiMenuItems, selectedKpis]);

    const onKpiCheckboxChange = (kpiId: KpiKey, checked: boolean) => {
        setDraftKpiIds((prev) => {
            if (!checked && mandatoryKpiSet.has(kpiId)) return prev;
            if (checked) return Array.from(new Set([...prev, kpiId]));
            const nextIds = prev.filter((id) => id !== kpiId);
            return nextIds.length ? nextIds : prev;
        });
    };
    const onKpiSelectAllToggle = () => {
        if (allKpisSelected) {
            const mandatorySelection = orderedKpiItems
                .map((kpi) => kpi.key)
                .filter((kpiId) => mandatoryKpiSet.has(kpiId));
            if (mandatorySelection.length) {
                setDraftKpiIds(mandatorySelection);
                return;
            }
            const fallbackId = draftKpiOrder[0] ?? kpiMenuItems[0]?.key;
            if (fallbackId) setDraftKpiIds([fallbackId]);
            return;
        }
        setDraftKpiIds(orderedKpiItems.map((kpi) => kpi.key));
    };
    const onKpiDragStart = (event: DragEvent<HTMLDivElement>, kpiId: KpiKey) => {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', kpiId);
        setDraggingKpiId(kpiId);
        setDraggingOverKpiId(kpiId);
    };
    const onKpiDragOver = (event: DragEvent<HTMLDivElement>, kpiId: KpiKey) => {
        event.preventDefault();
        if (draggingOverKpiId !== kpiId) setDraggingOverKpiId(kpiId);
    };
    const onKpiDrop = (event: DragEvent<HTMLDivElement>, targetKpiId: KpiKey) => {
        event.preventDefault();
        const draggedKpiId = event.dataTransfer.getData('text/plain') || draggingKpiId;
        if (!draggedKpiId || draggedKpiId === targetKpiId) {
            setDraggingKpiId(null);
            setDraggingOverKpiId(null);
            return;
        }
        setDraftKpiOrder((prev) => {
            const currentOrder = prev.length ? prev : kpiMenuItems.map((kpi) => kpi.key);
            const from = currentOrder.indexOf(draggedKpiId);
            const to = currentOrder.indexOf(targetKpiId);
            if (from < 0 || to < 0) return currentOrder;
            const reordered = [...currentOrder];
            const [moved] = reordered.splice(from, 1);
            reordered.splice(to, 0, moved);
            return reordered;
        });
        setDraggingKpiId(null);
        setDraggingOverKpiId(null);
    };
    const onKpiDragEnd = () => {
        setDraggingKpiId(null);
        setDraggingOverKpiId(null);
    };
    const onKpiReset = () => {
        const nextOrder = kpiMenuItems.map((kpi) => kpi.key);
        const defaultKpiSet = new Set(defaultKpis);
        const defaultSelection = nextOrder.filter((kpiId) => defaultKpiSet.has(kpiId));
        const fallbackId = nextOrder[0];
        setDraftKpiOrder(nextOrder);
        setDraftKpiIds(defaultSelection.length ? defaultSelection : fallbackId ? [fallbackId] : []);
    };
    const onKpiApply = () => {
        const selectedSet = new Set(draftKpiIds);
        const orderedSelectedKpis = orderedKpiItems.map((kpi) => kpi.key).filter((kpiId) => selectedSet.has(kpiId));
        const fallbackId = orderedKpiItems[0]?.key;
        const nextSelection = orderedSelectedKpis.length ? orderedSelectedKpis : fallbackId ? [fallbackId] : [];
        if (nextSelection.length > 0) onKpiSelect(nextSelection);
        setIsKpiDropdownOpen(false);
    };

    return (
        <div className="flex items-center justify-between px-4 py-4">
            <h2 className="m-0 text-lg font-semibold leading-normal text-[#140B1D]">{title}</h2>
            <div className="flex items-center gap-3">
                <Dropdown
                    trigger={['click']}
                    open={isDimensionDropdownOpen}
                    onOpenChange={setIsDimensionDropdownOpen}
                    dropdownRender={() => (
                        <DimensionDropdownPanel className="w-[225px] rounded-lg border border-black/10 bg-white py-4 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_6px_16px_0px_rgba(0,0,0,0.08),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
                            <div className="px-4">
                                <Input
                                    value={dimensionSearch}
                                    onChange={(event) => setDimensionSearch(event.target.value)}
                                    placeholder="Search"
                                    suffix={<SearchOutlined className="text-black/45" />}
                                    className="h-8 !rounded-md"
                                />
                            </div>
                            <Divider className="!my-2" />
                            <div
                                className={`flex items-center gap-1 px-4 py-[6px] text-sm leading-4 align-start ${
                                    isAllDimensionsSelected || isDimensionSelectionLimitReached ? 'text-attentionRed' : 'text-black/65'
                                }`}
                            >
                                <ExclamationCircleOutlined
                                    className={
                                        isAllDimensionsSelected || isDimensionSelectionLimitReached
                                            ? 'text-attentionRed'
                                            : 'text-black/65'
                                    }
                                />
                                <span>{dimensionValidationMessage}</span>
                            </div>
                            <div className="max-h-[204px] overflow-y-auto px-4">
                                <div className="flex flex-col gap-1">
                                    {filteredDimensionItems.map((dimension) => {
                                        const isChecked = activeDimensions.includes(dimension.key);
                                        const isLastSelectedDimension = isChecked && selectedDimensionCount === 1;
                                        const isLockedDimension = lockedDimensionSet.has(dimension.key);
                                        const isSelectionLimitReached =
                                            !isChecked && selectedDimensionCount >= effectiveDimensionLimit;
                                        return (
                                            <Checkbox
                                                key={dimension.key}
                                                checked={isChecked}
                                                disabled={isLockedDimension || isSelectionLimitReached || isLastSelectedDimension}
                                                onChange={(event) => {
                                                    const nextDimensionIds = event.target.checked
                                                        ? [...activeDimensions, dimension.key]
                                                        : activeDimensions.filter((key) => key !== dimension.key);
                                                    onDimensionSelect(nextDimensionIds);
                                                }}
                                                className={`!text-sm !m-0 ${(isLockedDimension || isLastSelectedDimension) ? 'opacity-50' : ''}`}
                                            >
                                                <div className='text-base'>{dimension.label}</div>
                                            </Checkbox>
                                        );
                                    })}
                                </div>
                            </div>
                        </DimensionDropdownPanel>
                    )}
                >
                    <Button className={filterButtonClassName}>
                        <PlusOutlined className={filterButtonIconClassName} />
                        <div className="whitespace-nowrap font-medium">Dimension</div>
                        <div>
                            <Down />
                        </div>
                    </Button>
                </Dropdown>
                <Dropdown
                    trigger={['click']}
                    open={isKpiDropdownOpen}
                    onOpenChange={setIsKpiDropdownOpen}
                    dropdownRender={() => (
                        <KpiDropdownPanel className="w-[352px] rounded-lg border border-black/10 bg-[#fff] pt-5 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
                            <div className="px-4 pb-3 text-xs leading-normal text-[#9f9fa9]">
                                <div>Click to show or hide</div>
                                <div>Drag to change position</div>
                            </div>
                            <div className="px-4 pb-2">
                                <div className="mb-2 rounded border !border-borderGrey bg-white px-2 py-[5px]">
                                    <Checkbox
                                        checked={allKpisSelected}
                                        onChange={onKpiSelectAllToggle}
                                        className="!m-0 !text-sm !text-black/25"
                                    >
                                        <div className="text-[13px] leading-[22px] text-black">Select All</div>
                                    </Checkbox>
                                </div>
                                <div className="kpi-modal-scroll max-h-[232px] overflow-y-auto pr-[2px]">
                                    <div className="flex flex-col gap-2">
                                        {orderedKpiItems.map((kpi) => {
                                            const isChecked = draftKpiIds.includes(kpi.key);
                                            const isLastSelectedKpi = isChecked && draftKpiIds.length === 1;
                                            const isMandatoryKpi = mandatoryKpiSet.has(kpi.key);
                                            const isDropTarget = draggingOverKpiId === kpi.key && draggingKpiId !== kpi.key;
                                            return (
                                                <div
                                                    key={kpi.key}
                                                    draggable
                                                    onDragStart={(event) => onKpiDragStart(event, kpi.key)}
                                                    onDragOver={(event) => onKpiDragOver(event, kpi.key)}
                                                    onDrop={(event) => onKpiDrop(event, kpi.key)}
                                                    onDragEnd={onKpiDragEnd}
                                                    className={`flex items-center gap-2 rounded border !border-borderGrey bg-white px-2 py-[5px] ${isDropTarget ? 'border-[#44338a]' : 'border-black/6'} ${draggingKpiId === kpi.key ? 'opacity-70' : ''}`}
                                                >
                                                    <span className="inline-flex cursor-grab items-center text-[#44338a] active:cursor-grabbing">
                                                        <HolderOutlined />
                                                    </span>
                                                    <Checkbox
                                                        checked={isChecked}
                                                        disabled={isLastSelectedKpi || isMandatoryKpi}
                                                        onChange={(event) => onKpiCheckboxChange(kpi.key, event.target.checked)}
                                                        className={`!m-0 !text-sm ${(isLastSelectedKpi || isMandatoryKpi) ? 'opacity-50' : ''}`}
                                                    >
                                                        <div className="text-[13px] leading-[22px] text-black/88">{kpi.label}</div>
                                                    </Checkbox>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-2">
                                <Button
                                    onClick={onKpiReset}
                                    className="!h-8 !rounded-[8px] !border-[#d9d9d9] !bg-white !px-4 !text-[14px] !font-semibold !text-black hover:!border-[#d9d9d9] hover:!bg-white"
                                >
                                    Reset
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={onKpiApply}
                                    className="!h-8 !rounded-[8px] !border-[#44338a] !bg-[#44338a] !px-[16px] !text-[14px] !font-normal !text-white"
                                >
                                    Apply
                                </Button>
                            </div>
                        </KpiDropdownPanel>
                    )}
                >
                    <Button className={filterButtonClassName}>
                        <PlusOutlined className={filterButtonIconClassName} />
                        <div className="whitespace-nowrap font-medium">KPIs</div>
                        <div>
                            <Down />
                        </div>
                    </Button>
                </Dropdown>
            </div>
        </div>
    );
};

const PerformanceByLensSection = forwardRef<PerformanceByLensExportHandle, PerformanceByLensSectionProps>(({
    metaFeature,
    drilldownFilters = [],
}, ref) => {
    const lensTabConfigs = useMemo(() => metaFeature?.options?.tabs ?? [], [metaFeature]);
    const lensTabs = useMemo(() => {
        return lensTabConfigs.map((tab) => ({ key: tab.id, label: tab.label }));
    }, [lensTabConfigs]);
    const defaultLensTab = useMemo(
        () => lensTabConfigs.find((tab) => tab.is_default)?.id ?? lensTabConfigs[0]?.id ?? '',
        [lensTabConfigs]
    );

    const [activeDimensions, setActiveDimensions] = useState<OverallPerformanceDimensionKey[]>([]);
    const [activeLensTab, setActiveLensTab] = useState(defaultLensTab);
    const [selectedKpis, setSelectedKpis] = useState<KpiKey[]>([]);
    const [dimensionsByTab, setDimensionsByTab] = useState<Record<string, OverallPerformanceDimensionKey[]>>({});
    const [kpisByTab, setKpisByTab] = useState<Record<string, KpiKey[]>>({});
    const [kpiInputValuesByTab, setKpiInputValuesByTab] = useState<Record<string, Record<string, string>>>({});
    const [orderBy, setOrderBy] = useState<PerformanceSummaryOrderByItem[] | undefined>(undefined);

    const activeTabConfig = useMemo(
        () => lensTabConfigs.find((tab) => tab.id === activeLensTab) ?? lensTabConfigs[0],
        [activeLensTab, lensTabConfigs]
    );

    const dimensionMenuItems: DropdownItem[] = (activeTabConfig?.group_by_options ?? []).map((dimension) => ({
        key: getDimensionOptionKey(dimension),
        label: dimension.label,
        is_default: dimension.is_default,
    }));

    const lockedDimensionIds = useMemo(
        () =>
            (activeTabConfig?.group_by_options ?? [])
                .filter((dimension) => isMandatoryOption(dimension))
                .map((dimension) => getDimensionOptionKey(dimension)),
        [activeTabConfig]
    );
    const selectedDimensionConfigs = useMemo(() => {
        const dimensionsById = new Map(
            (activeTabConfig?.group_by_options ?? []).map((option) => [getDimensionOptionKey(option), option])
        );
        return activeDimensions.map((id) => dimensionsById.get(id)).filter((option): option is MetaOption => Boolean(option));
    }, [activeDimensions, activeTabConfig]);

    const resolvedGranularityId = useMemo(
        () => resolvePpaGranularityId(activeTabConfig?.granularity_column_mapping, selectedDimensionConfigs),
        [activeTabConfig, selectedDimensionConfigs]
    );

    /** Tab narrowed to granularity-valid KPIs; reused by normalizeKpiSelection call sites. */
    const granularityScopedTabConfig = useMemo<MetaTab | undefined>(() => {
        if (!activeTabConfig || resolvedGranularityId == null) return activeTabConfig;
        return {
            ...activeTabConfig,
            column_options: filterPpaKpisByGranularity(activeTabConfig.column_options ?? [], resolvedGranularityId),
        };
    }, [activeTabConfig, resolvedGranularityId]);
    const availableKpiOptions = useMemo(
        () => granularityScopedTabConfig?.column_options ?? [],
        [granularityScopedTabConfig]
    );
    const defaultKpis = useMemo(
        () =>
            normalizeKpiSelection(
                availableKpiOptions.filter((option) => option.is_default).map((option) => option.id),
                granularityScopedTabConfig
            ),
        [availableKpiOptions, granularityScopedTabConfig]
    );
    const mandatoryKpis = useMemo(
        () =>
            (activeTabConfig?.column_options ?? [])
                .filter((option) => option.is_mandatory || option.mandatory || option.isMandatory || option.is_required)
                .map((option) => option.id),
        [activeTabConfig]
    );
    const kpiMenuItems: DropdownItem[] = availableKpiOptions.map((kpi) => ({
        key: kpi.id,
        label: kpi.label,
    }));
    const kpiOptionById = useMemo(() => {
        const optionById = new Map<string, MetaOption>();
        availableKpiOptions.forEach((option) => {
            const existing = optionById.get(option.id);
            const hasInputConfig = Boolean(getPrimaryInputConfig(option.input_list));
            const existingHasInputConfig = Boolean(getPrimaryInputConfig(existing?.input_list));
            if (!existing || (hasInputConfig && !existingHasInputConfig)) optionById.set(option.id, option);
        });
        return optionById;
    }, [availableKpiOptions]);

    const selectedAggregateAttributeTypes = useMemo(
        () => selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.id).filter(Boolean),
        [selectedDimensionConfigs]
    );
    const selectedAggregateDimensions = useMemo(
        () => selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.dimension),
        [selectedDimensionConfigs]
    );

    const selectedAggregateIsAttributes = useMemo(
        () => selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.is_attribute),
        [selectedDimensionConfigs]
    );
    const activeTabInputValues = useMemo(
        () => (activeLensTab ? kpiInputValuesByTab[activeLensTab] ?? {} : {}),
        [activeLensTab, kpiInputValuesByTab]
    );
    const lensUserInputs = useMemo(
        () =>
            selectedKpis
                .map((kpiId) => {
                    const inputConfig = getPrimaryInputConfig(kpiOptionById.get(kpiId)?.input_list);
                    if (!inputConfig?.input_id) return null;
                    const rawValue = activeTabInputValues[kpiId] ?? getDefaultInputValue(inputConfig);
                    const numericValue = Number(rawValue);
                    if (!Number.isFinite(numericValue)) return null;
                    return {
                        input_id: inputConfig.input_id,
                        input_val: numericValue,
                    };
                })
                .filter((input): input is { input_id: string; input_val: number } => Boolean(input)),
        [activeTabInputValues, kpiOptionById, selectedKpis]
    );
    const lensFeatureMeta = useMemo(
        () =>
            activeLensTab
                ? {
                      feature_id: metaFeature?.feature_id ?? 'performance_by_lens',
                      tab: {
                          id_type: 'lens',
                          id: activeLensTab,
                      },
                  }
                : undefined,
        [activeLensTab, metaFeature?.feature_id]
    );

    useEffect(() => {
        if (!lensTabs.some((tab) => tab.key === activeLensTab)) {
            setActiveLensTab(defaultLensTab);
        }
    }, [activeLensTab, defaultLensTab, lensTabs]);

    useEffect(() => {
        if (!activeLensTab) return;
        setKpiInputValuesByTab((previousState) => {
            const currentTabValues = previousState[activeLensTab] ?? {};
            const nextTabValues = { ...currentTabValues };
            let didChange = false;

            selectedKpis.forEach((kpiId) => {
                if (nextTabValues[kpiId] !== undefined) return;
                const inputConfig = getPrimaryInputConfig(kpiOptionById.get(kpiId)?.input_list);
                if (!inputConfig) return;
                nextTabValues[kpiId] = getDefaultInputValue(inputConfig);
                didChange = true;
            });

            if (!didChange) return previousState;
            return {
                ...previousState,
                [activeLensTab]: nextTabValues,
            };
        });
    }, [activeLensTab, kpiOptionById, selectedKpis]);

    useEffect(() => {
        if (!activeTabConfig?.id) {
            if (activeDimensions.length) setActiveDimensions([]);
            if (selectedKpis.length) setSelectedKpis([]);
            return;
        }

        const tabId = activeTabConfig.id;
        const persistedDimensions = dimensionsByTab[tabId] ?? [];
        const nextDimensions = normalizeDimensionSelection(persistedDimensions, activeTabConfig);
        if (!areStringListsEqual(activeDimensions, nextDimensions)) {
            setActiveDimensions(nextDimensions);
        }
        if (!areStringListsEqual(dimensionsByTab[tabId] ?? [], nextDimensions)) {
            setDimensionsByTab((previousState) => ({ ...previousState, [tabId]: nextDimensions }));
        }

        const persistedKpis = kpisByTab[tabId] ?? [];
        const nextKpis = normalizeKpiSelection(persistedKpis, granularityScopedTabConfig);
        if (!areStringListsEqual(selectedKpis, nextKpis)) {
            setSelectedKpis(nextKpis);
        }
        if (!areStringListsEqual(kpisByTab[tabId] ?? [], nextKpis)) {
            setKpisByTab((previousState) => ({ ...previousState, [tabId]: nextKpis }));
        }
    }, [activeDimensions, activeTabConfig, granularityScopedTabConfig, dimensionsByTab, kpisByTab, selectedKpis]);

    const {
        data: kpiInfiniteData,
        isLoading: isKpisLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
        isFetching
    } = usePerformanceSummaryKpis<KpiNode>({
        queryKey: ['performance-by-lens-kpis', activeLensTab, activeDimensions, selectedKpis, drilldownFilters],
        kpiIds: selectedKpis,
        dimension: selectedAggregateDimensions[0] ?? activeTabConfig?.id,
        aggregateAttributeTypes: selectedAggregateAttributeTypes,
        aggregateDimensions: selectedAggregateDimensions,
        aggregateIsAttributes: selectedAggregateIsAttributes,
        drilldownFilters,
        orderBy,
        userInputs: lensUserInputs,
        featureMeta: lensFeatureMeta,
        normalizeData: (rows) =>
            normalizePpaKpiRows<KpiItem>(rows, (kpi) => ({
                kpi_id: kpi.kpi_id,
                value: kpi.value,
                raw_value: kpi.raw_value,
                valuePrefix: kpi.valuePrefix,
                valueSuffix: kpi.valueSuffix,
                comparison_period: kpi.comparison_period,
                growth_percentage: kpi.growth_percentage,
                trend_direction: kpi.trend_direction,
            })) as KpiNode[],
        enabled: Boolean(activeTabConfig?.id && selectedAggregateAttributeTypes.length && selectedKpis.length > 0),
    });

    const sourceRows = useMemo(
        () => kpiInfiniteData?.pages.flatMap((page) => page.nodes) ?? [],
        [kpiInfiniteData?.pages],
    );

    const totalRecordCount = kpiInfiniteData?.pages?.[0]?.total;

    const lensRows = useMemo(
        () => toLensRows(sourceRows, selectedKpis, selectedDimensionConfigs),
        [selectedDimensionConfigs, selectedKpis, sourceRows]
    );
    const comparisonPeriodByKpiId = useMemo(() => getComparisonPeriodByKpiId(sourceRows), [sourceRows]);

    const hasDrilldownFilters = drilldownFilters.length > 0;
    const lensColumns = useMemo<ColumnsType<LensTableRow>>(() => {
        const setLensAttributeSort = (dimensionConfig: MetaOption, direction: 'asc' | 'desc') => {
            setOrderBy([{ id_type: 'attribute_type', id: dimensionConfig.id, direction }]);
        };

        const setLensKpiSort = (kpiId: string, direction: 'asc' | 'desc') => {
            setOrderBy([{ id_type: 'kpi_id', id: kpiId, direction }]);
        };

        const dimensionColumns = selectedDimensionConfigs.map((dimensionConfig) => {
            const dimensionKey = getDimensionOptionKey(dimensionConfig);
            const dimensionTitle = `${dimensionConfig.label}${dimensionKey === activeDimensions[0] ? ` (${lensRows.length})` : ''}`;
            const sortActive = lensAttributeSortActive(orderBy, dimensionConfig);
            return {
                title: (
                    <HeaderTitle
                        title={dimensionTitle}
                        sortActive={sortActive}
                        onSortAsc={() => setLensAttributeSort(dimensionConfig, 'asc')}
                        onSortDesc={() => setLensAttributeSort(dimensionConfig, 'desc')}
                    />
                ),
                key: `dimension-${dimensionKey}`,
                width: 220,
                fixed: 'left' as const,
                onCell: () => ({
                    style: hasDrilldownFilters ? { backgroundColor: '#F7F5FF' } : {},
                }),
                render: (_: unknown, row: LensTableRow) => (
                    <span className={row.isTotal ? 'font-semibold text-black' : 'font-normal text-black'}>
                        {row.dimensionValues[dimensionKey] ?? '-'}
                    </span>
                ),
            };
        });
        const selectedKpiOptions = selectedKpis
            .map((kpiId) => kpiOptionById.get(kpiId))
            .filter((kpi): kpi is MetaOption => Boolean(kpi));

        return [
            ...dimensionColumns,
            ...selectedKpiOptions.map((kpi) => {
                const inputConfig = getPrimaryInputConfig(kpi.input_list);
                const kpiSortActive = lensKpiSortActive(orderBy, kpi.id);
                const comparisonPeriodLabel = formatComparisonPeriodLabel(
                    comparisonPeriodByKpiId.get(kpi.id) ?? kpi.comparison_period
                );
                const kpiHeaderTitle = kpi.label;
                return {
                    title: inputConfig ? (
                        <InputKpiHeader
                            title={kpiHeaderTitle}
                            comparisonLabel={comparisonPeriodLabel}
                            value={activeTabInputValues[kpi.id] ?? getDefaultInputValue(inputConfig)}
                            placeholder={inputConfig.placeholder}
                            onChange={(nextValue) =>
                                setKpiInputValuesByTab((previousState) => {
                                    if (!activeLensTab) return previousState;
                                    const currentTabValues = previousState[activeLensTab] ?? {};
                                    return {
                                        ...previousState,
                                        [activeLensTab]: {
                                            ...currentTabValues,
                                            [kpi.id]: nextValue,
                                        },
                                    };
                                })
                            }
                            onBlur={() =>
                                setKpiInputValuesByTab((previousState) => {
                                    if (!activeLensTab) return previousState;
                                    const currentTabValues = previousState[activeLensTab] ?? {};
                                    return {
                                        ...previousState,
                                        [activeLensTab]: {
                                            ...currentTabValues,
                                            [kpi.id]: normalizeInputValue(currentTabValues[kpi.id] ?? '', inputConfig),
                                        },
                                    };
                                })
                            }
                            onStepChange={(delta) =>
                                setKpiInputValuesByTab((previousState) => {
                                    if (!activeLensTab) return previousState;
                                    const currentTabValues = previousState[activeLensTab] ?? {};
                                    return {
                                        ...previousState,
                                        [activeLensTab]: {
                                            ...currentTabValues,
                                            [kpi.id]: bumpInputValue(currentTabValues[kpi.id] ?? '', delta, inputConfig),
                                        },
                                    };
                                })
                            }
                            sortActive={kpiSortActive}
                            onSortAsc={() => setLensKpiSort(kpi.id, 'asc')}
                            onSortDesc={() => setLensKpiSort(kpi.id, 'desc')}
                        />
                    ) : (
                        <HeaderTitle
                            title={kpiHeaderTitle}
                            comparisonLabel={comparisonPeriodLabel}
                            sortActive={kpiSortActive}
                            onSortAsc={() => setLensKpiSort(kpi.id, 'asc')}
                            onSortDesc={() => setLensKpiSort(kpi.id, 'desc')}
                        />
                    ),
                    dataIndex: ['kpiValues', kpi.id],
                    key: kpi.id,
                    width: inputConfig ? 250 : 200,
                    align: 'left' as const,
                    onCell: () => ({
                        style: hasDrilldownFilters ? { backgroundColor: '#F7F5FF' } : {},
                    }),
                    render: (_value: string, row: LensTableRow) => (
                        <TrendMetricCell metric={row.metrics[kpi.id] ?? DEFAULT_METRIC} isTotal={row.isTotal} />
                    ),
                };
            }),
        ];
    }, [
        activeDimensions,
        hasDrilldownFilters,
        activeTabInputValues,
        comparisonPeriodByKpiId,
        kpiOptionById,
        lensRows.length,
        orderBy,
        selectedDimensionConfigs,
        selectedKpis,
    ]);

    const tableScrollX = useMemo(
        () => lensColumns.reduce((acc, column) => acc + (typeof column.width === 'number' ? column.width : 180), 0),
        [lensColumns]
    );
    const isInitialLoading = isKpisLoading && lensRows.length === 0;
    const isLoadingMore = isFetchingNextPage;
    const isTableLoading = isInitialLoading || (isFetching && !isLoadingMore);
    const handleLoadMore = () => {
        if (!hasNextPage || isLoadingMore) return;
        void fetchNextPage();
    };

    useImperativeHandle(
        ref,
        () => ({
            getExportData: () => {
                const selectedDimensions = selectedDimensionConfigs.map((dimensionConfig) => dimensionConfig.label);
                const selectedKpiLabels = selectedKpis.map((kpiId) => kpiOptionById.get(kpiId)?.label ?? kpiId);
                const headers = [...selectedDimensions, ...selectedKpiLabels];
                const rows = lensRows.map((row) => {
                    const dimensionCells = selectedDimensionConfigs.map((dimensionConfig) => {
                        const dimensionKey = getDimensionOptionKey(dimensionConfig);
                        return row.dimensionValues[dimensionKey] ?? '-';
                    });
                    const kpiCells = selectedKpis.map((kpiId) => {
                        const rawValue = row.metrics[kpiId]?.raw_value;
                        if (rawValue == null) return '';
                        const normalizedRawValue = String(rawValue).trim();
                        if (!normalizedRawValue || normalizedRawValue === '-') return '';
                        return formatRawMetricForExport(normalizedRawValue);
                    });
                    return [...dimensionCells, ...kpiCells];
                });
                return { headers, rows };
            },
        }),
        [kpiOptionById, lensRows, selectedDimensionConfigs, selectedKpis]
    );

    const handleDimensionSelect = (keys: OverallPerformanceDimensionKey[]) => {
        const nextDimensions = normalizeDimensionSelection(keys, activeTabConfig);
        setActiveDimensions(nextDimensions);
        setOrderBy(undefined);

        if (activeTabConfig?.id) {
            setDimensionsByTab((previousState) => ({ ...previousState, [activeTabConfig.id]: nextDimensions }));
        }
    };

    const handleKpiSelectionChange = (keys: KpiKey[]) => {
        const nextKpis = normalizeKpiSelection(keys, granularityScopedTabConfig);
        setSelectedKpis(nextKpis);
        setOrderBy(undefined);

        if (activeTabConfig?.id) {
            setKpisByTab((previousState) => ({ ...previousState, [activeTabConfig.id]: nextKpis }));
        }
    };

    return (
        <div className="w-full border border-borderGrey rounded-lg">
            <PerformanceSectionHeader
                title="Performance by Lens"
                activeDimensions={activeDimensions}
                dimensionMenuItems={dimensionMenuItems}
                lockedDimensionIds={lockedDimensionIds}
                onDimensionSelect={handleDimensionSelect}
                selectedKpis={selectedKpis}
                kpiMenuItems={kpiMenuItems}
                defaultKpis={defaultKpis}
                mandatoryKpis={mandatoryKpis}
                onKpiSelect={handleKpiSelectionChange}
            />
            <StyledSectionTabs
                type="card"
                size="small"
                activeKey={activeLensTab}
                onChange={(key) => {
                    setOrderBy(undefined);
                    setActiveLensTab(key);
                }}
                items={lensTabs}
            />
            <StyledInfiniteTableWrapper>
                <InfiniteScroller
                    data={lensRows}
                    loading={isInitialLoading || isLoadingMore}
                    hasMore={Boolean(hasNextPage)}
                    height={PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT}
                    onNext={handleLoadMore}
                >
                    <Table
                        columns={lensColumns}
                        dataSource={lensRows}
                        pagination={false}
                        bordered={false}
                        loading={isTableLoading}
                        scroll={{ x: tableScrollX, y: PERFORMANCE_SUMMARY_INFINITE_SCROLL_HEIGHT }}
                        style={{
                            opacity: isFetchingNextPage ? 0.7 : 1,
                            transition: 'opacity 0.2s',
                        }}
                        onRow={() => ({
                            style: hasDrilldownFilters ? { backgroundColor: '#F7F5FF' } : {},
                        })}
                        locale={{
                            emptyText: (
                                <div className="flex font-md text-borderGrey justify-center items-center">
                                    No data available
                                </div>
                            ),
                        }}
                    />
                </InfiniteScroller>
            </StyledInfiniteTableWrapper>
            <div className="rounded-[3px]">
                <div className={recordSummaryClassName}>
                    Showing {lensRows.length}
                    {totalRecordCount !== undefined ? ` of ${totalRecordCount}` : ''} records
                </div>
            </div>
        </div>
    );
});

export default PerformanceByLensSection;
